/*
 * Type-to-filter for the country/market dropdown rendered by
 * snippets/multi-selectors.liquid (header and footer).
 *
 * theme.Disclosure (theme.js) still owns open/close and the localization form
 * submit; this script only shows/hides rows and keeps keyboard focus sensible.
 *
 * Matching is case- and accent-insensitive against the visible localized
 * name, the ISO country code, and (where the browser supports
 * Intl.DisplayNames) the country name in every store locale listed in
 * data-country-filter-langs — so "lit" finds "Lietuva" on the LT storefront.
 */
(function () {
  'use strict';

  var VISIBLE_CLASS = 'disclosure-list--visible';
  var INIT_FLAG = 'countryFilterReady';

  // Fold case and strip diacritics. NFD separates base letters from combining
  // marks (ą č ę ė į š ų ū ž, ä ö ü, é…) which we then drop; the handful of
  // letters that do not decompose are mapped by hand.
  var SPECIALS = { 'ß': 'ss', 'ø': 'o', 'œ': 'oe', 'æ': 'ae', 'ł': 'l', 'đ': 'd', 'ð': 'd', 'þ': 'th', 'ı': 'i' };
  function fold(str) {
    var s = String(str || '').toLowerCase();
    if (s.normalize) s = s.normalize('NFD');
    s = s.replace(/[̀-ͯ]/g, '');
    s = s.replace(/[ßøœæłđðþı]/g, function (c) { return SPECIALS[c] || c; });
    return s.replace(/\s+/g, ' ').trim();
  }

  var displayNameCache = {};
  function displayNames(lang) {
    if (displayNameCache[lang] !== undefined) return displayNameCache[lang];
    var dn = null;
    try {
      if (window.Intl && Intl.DisplayNames) {
        dn = new Intl.DisplayNames([lang], { type: 'region' });
      }
    } catch (e) { dn = null; }
    displayNameCache[lang] = dn;
    return dn;
  }

  function buildHaystack(item, langs) {
    var parts = [item.getAttribute('data-country-name') || item.textContent, item.getAttribute('data-country-code') || ''];
    var code = item.getAttribute('data-country-code');
    if (code) {
      for (var i = 0; i < langs.length; i++) {
        var dn = displayNames(langs[i]);
        if (!dn) continue;
        try {
          var name = dn.of(code);
          if (name && name !== code) parts.push(name);
        } catch (e) { /* unknown region code */ }
      }
    }
    return fold(parts.join(' | '));
  }

  function setup(list) {
    if (list.dataset[INIT_FLAG]) return;
    list.dataset[INIT_FLAG] = '1';

    var input = list.querySelector('[data-country-filter-input]');
    var empty = list.querySelector('[data-country-filter-empty]');
    var items = Array.prototype.slice.call(list.querySelectorAll('[data-country-filter-item]'));
    if (!input || !items.length) return;

    var langs = (list.getAttribute('data-country-filter-langs') || '')
      .split(',').map(function (l) { return l.trim(); }).filter(Boolean);
    if (langs.indexOf('en') === -1) langs.push('en');

    var haystacks = null; // built lazily on first keystroke
    var swallowEscapeKeyup = false;

    function ensureIndex() {
      if (haystacks) return;
      haystacks = items.map(function (item) { return buildHaystack(item, langs); });
    }

    function visibleItems() {
      return items.filter(function (item) { return !item.hidden; });
    }

    function applyFilter() {
      var terms = fold(input.value).split(' ').filter(Boolean);
      if (!terms.length) {
        items.forEach(function (item) { item.hidden = false; });
        if (empty) empty.hidden = true;
        return;
      }
      ensureIndex();
      var shown = 0;
      items.forEach(function (item, i) {
        var hay = haystacks[i];
        var ok = true;
        for (var t = 0; t < terms.length; t++) {
          if (hay.indexOf(terms[t]) === -1) { ok = false; break; }
        }
        item.hidden = !ok;
        if (ok) shown++;
      });
      if (empty) empty.hidden = shown > 0;
    }

    function clearFilter() {
      input.value = '';
      applyFilter();
    }

    function focusOption(item) {
      var link = item && item.querySelector('[data-disclosure-option]');
      if (link) link.focus();
    }

    // --- input: filter as you type, Escape clears before it closes ---------
    input.addEventListener('input', applyFilter);

    input.addEventListener('keydown', function (event) {
      var key = event.key;
      if (key === 'Escape' || key === 'Esc') {
        if (input.value) {
          // First Escape clears the filter; the Disclosure keyup handler
          // (which closes the list) must not see this one.
          event.preventDefault();
          clearFilter();
          swallowEscapeKeyup = true;
        }
        return;
      }
      if (key === 'ArrowDown' || key === 'Down') {
        var first = visibleItems()[0];
        if (first) { event.preventDefault(); focusOption(first); }
        return;
      }
      if (key === 'Enter') {
        // Enter inside the form would submit it with the *current* country
        // (a pointless reload). Pick the first visible match instead.
        event.preventDefault();
        var hit = visibleItems()[0];
        var link = hit && hit.querySelector('[data-disclosure-option]');
        if (link) link.click();
      }
    });

    input.addEventListener('keyup', function (event) {
      if ((event.key === 'Escape' || event.key === 'Esc' || event.which === 27) && swallowEscapeKeyup) {
        swallowEscapeKeyup = false;
        event.stopPropagation();
      }
    });

    // --- options: arrow navigation over visible rows, type to refocus -----
    list.addEventListener('keydown', function (event) {
      var link = event.target.closest && event.target.closest('[data-disclosure-option]');
      if (!link || !list.contains(link)) return;
      var item = link.closest('[data-country-filter-item]');
      if (!item) return;
      var key = event.key;
      if (key === 'ArrowDown' || key === 'Down' || key === 'ArrowUp' || key === 'Up') {
        event.preventDefault();
        var vis = visibleItems();
        var idx = vis.indexOf(item);
        if (key === 'ArrowDown' || key === 'Down') {
          if (idx < vis.length - 1) focusOption(vis[idx + 1]);
        } else if (idx > 0) {
          focusOption(vis[idx - 1]);
        } else {
          input.focus();
        }
        return;
      }
      // A printable character while a row is focused: send it to the box.
      if (key && key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        input.focus();
      } else if (key === 'Backspace') {
        event.preventDefault();
        input.focus();
        input.value = input.value.slice(0, -1);
        applyFilter();
      }
    });

    // Mouse/touch on a row must not blur the input: on Safari and touch
    // devices links do not take focus, so the input's focusout would reach
    // theme.Disclosure with relatedTarget=null and hide the list before the
    // click lands. Cancelling mousedown keeps focus where it is; click still
    // fires and the Disclosure submits the form as before.
    list.addEventListener('mousedown', function (event) {
      var link = event.target.closest && event.target.closest('[data-disclosure-option]');
      if (link && list.contains(link)) event.preventDefault();
    });

    // --- phones: pin the list full-width under the header -----------------
    // The CSS (multi-selectors.liquid) makes the list position:fixed below
    // 769px and reads --twe-country-list-top. The header is static on load
    // but becomes position:fixed + transform when "stuck", and a transformed
    // ancestor turns into the containing block for fixed descendants, so we
    // measure rather than assume: with top:0 the list's own rect.top IS the
    // containing block's top, whatever it currently is.
    var mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
    var header = list.closest ? list.closest('.site-header') : null;
    var toggle = list.parentNode && list.parentNode.querySelector('[data-disclosure-toggle]');
    var positionFrame = null;

    var HEADER_GAP = 0;    // list sits flush under the header (Danys: no gap)
    var BOTTOM_GAP = 12;   // keep the list clear of the keyboard / toolbar edge
    var MIN_HEIGHT = 140;  // never collapse below ~2 rows + the search box

    function positionForMobile() {
      positionFrame = null;
      if (!mobileQuery || !mobileQuery.matches) {
        list.style.removeProperty('--twe-country-list-top');
        list.style.removeProperty('--twe-country-list-max');
        return;
      }
      list.style.setProperty('--twe-country-list-top', '0px');
      var containingTop = list.getBoundingClientRect().top;
      var anchor = header || toggle;
      var anchorBottom = anchor ? anchor.getBoundingClientRect().bottom : 0;
      // Where the list's top lands, in layout-viewport coordinates.
      var listTop = Math.max(0, anchorBottom + HEADER_GAP);
      list.style.setProperty('--twe-country-list-top', Math.round(listTop - containingTop) + 'px');

      // Height that is actually visible. With the iOS keyboard up the visual
      // viewport shrinks (and may be offset) while innerHeight/100dvh do not.
      var vv = window.visualViewport;
      var visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      var maxHeight = Math.round(visibleBottom - listTop - BOTTOM_GAP);
      list.style.setProperty('--twe-country-list-max', Math.max(MIN_HEIGHT, maxHeight) + 'px');
    }

    function schedulePosition() {
      if (positionFrame !== null) return;
      positionFrame = window.requestAnimationFrame
        ? window.requestAnimationFrame(positionForMobile)
        : setTimeout(positionForMobile, 16);
    }

    function watchViewport(on) {
      var method = on ? 'addEventListener' : 'removeEventListener';
      window[method]('scroll', schedulePosition, { passive: true });
      window[method]('resize', schedulePosition);
      if (window.visualViewport) {
        window.visualViewport[method]('resize', schedulePosition);
        window.visualViewport[method]('scroll', schedulePosition);
      }
    }

    // --- open/close: reset and focus when the Disclosure shows the list ----
    var wasVisible = list.classList.contains(VISIBLE_CLASS);
    function onVisibilityChange() {
      var isVisible = list.classList.contains(VISIBLE_CLASS);
      if (isVisible === wasVisible) return;
      wasVisible = isVisible;
      clearFilter();
      watchViewport(isVisible);
      if (isVisible) {
        positionForMobile();
        // Reset scroll (a previous session may have left it mid-list) and
        // hand focus to the box so typing works straight away.
        list.scrollTop = 0;
        try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
        // iOS scrolls the list a few rows when the keyboard slides in, which
        // leaves the (sticky) search box floating mid-list. Pin it back once
        // the keyboard animation has run.
        setTimeout(pinToTop, 120);
        setTimeout(pinToTop, 450);
      }
    }

    function pinToTop() {
      if (list.classList.contains(VISIBLE_CLASS) && !input.value) list.scrollTop = 0;
    }
    input.addEventListener('focus', function () { setTimeout(pinToTop, 0); });
    if (window.MutationObserver) {
      new MutationObserver(onVisibilityChange).observe(list, { attributes: true, attributeFilter: ['class'] });
    } else {
      // Ancient fallback: poll the class on toggle clicks.
      var toggle = list.parentNode && list.parentNode.querySelector('[data-disclosure-toggle]');
      if (toggle) toggle.addEventListener('click', function () { setTimeout(onVisibilityChange, 0); });
    }
  }

  function init(root) {
    var lists = (root || document).querySelectorAll('[data-country-filter]');
    Array.prototype.forEach.call(lists, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Theme editor re-renders header/footer sections; pick up the new markup.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
